import React, { PureComponent, Fragment } from 'react';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { injectIntl, FormattedMessage, intlShape } from 'react-intl';
import { withFormik } from 'formik';
import * as Yup from 'yup';

import find from 'lodash/find';
import unionBy from 'lodash/unionBy';

import LanguageSelect from 'app/lang/LanguageSelect';
import RegisterSchoolSelect from './RegisterSchoolSelect';
import LoginSignTabs from '../login/LoginSignTabs';
import { EditionStatus } from './registerReducer';

import { Input, Select, Row, Col, Alert, Button, Loader } from 'sa-ui';

import messages from './registerMessages';

import './RegisterForm.scss';

const ID_COUNTRY_BRAZIL = 14;
const ID_COUNTRY_COLOMBIA = 15;
const ID_USER_TYPE_TEACHER = '3';
const ID_USER_TYPE_COORDINATOR = '2';
const ID_USER_TYPE_PUBLIC = '2';
const ID_USER_TYPE_PRIVATE = '1';
const idsCountries = [ID_COUNTRY_BRAZIL];

const RegisterSchema = Yup.object().shape({
  firstname: Yup.string().required('errorRequired'),
  lastname: Yup.string().required('errorRequired'),
  idSchool: Yup.number().required('errorRequired'),
  idCountry: Yup.number().required('errorRequired'),
  email: Yup.string()
    .email('errorInvalidEmail')
    .required('errorRequired'),
  emailConfirm: Yup.string()
    .email('errorInvalidEmail')
    .oneOf([Yup.ref('email'), null], 'errorDontMatch')
    .required('errorRequired'),
  password: Yup.string().required('errorRequired'),
  passwordConfirm: Yup.string()
    .oneOf([Yup.ref('password'), null], 'errorDontMatch')
    .required('errorRequired'),
  city: Yup.string().required('errorRequired'),
});

const initialValues = {
  firstname: '',
  lastname: '',
  email: '',
  emailConfirm: '',
  password: '',
  passwordConfirm: '',
  city: '',
  state: '',
  idSchool: '',
  profile: '',
  courses: [],
  selectedGroups: [],
};

const enhanceForm = withFormik({
  mapPropsToValues: () => ({}),
  initialValues,
  validationSchema: RegisterSchema,
  handleSubmit: (values, { props }) => {
    props.onSubmit(values);
  },
});

class RegisterForm extends PureComponent {
  state = {
    school: null,
    groups: [],
    selectedGroups: [],
    isLoading: true,
  };

  componentDidMount() {
    setTimeout(() => {
      this.handleSelectCountry();
      this.setState({ isLoading: false });
    }, 1000);
  }

  componentDidUpdate(prevProps) {
    const { intl, values } = this.props;

    if (prevProps.values.idCountry !== values.idCountry) {
      this.props.getStates(values.idCountry);
    }

    if (prevProps.values.state !== values.state) {
      this.props.getCities(values.idCountry, values.state);
    }

    if (prevProps.values.city !== values.city) {
      this.props.searchSchools(values.idCountry, values.state, values.city);
    }
  }

  getMessageError = field => {
    const { intl, errors } = this.props;
    if (errors && errors[field]) {
      return intl.formatMessage(messages[errors[field]]);
    }
    return undefined;
  };

  isValid = field => {
    const { errors, error, touched } = this.props;
    if (errors && errors[field] && touched[field]) {
      return false;
    }
    if (Object.keys(error).length > 0) return false;
    return touched[field] ? true : undefined;
  };

  handleSelectSchool = school => {
    this.setState({ school });
    this.props.setFieldValue('idSchool', school.id);
  };

  handleSelectCountry = event => {
    let value = '14';
    // let value = this.props.values.idCountry;
    if (event) {
      value = event.target.value;
    }

    let groups = [];

    const selectedCountry = find(this.props.selectCountries, el => el.id === value);
    const selectedCountryDetails = this.props.countries[selectedCountry.label];
    const selectedCountryGroups = unionBy(selectedCountryDetails, 'groups').find(el => el.groups);
    if (selectedCountryGroups && selectedCountryGroups.groups) {
      groups = selectedCountryGroups.groups.map(el => ({
        ...el,
        value: el.id,
        label: el.name,
      }));
    }

    this.setState({ groups });

    this.props.setFieldValue('idCountry', value);
    this.props.setFieldValue('state', initialValues.state);
    this.props.setFieldValue('city', initialValues.city);
    this.props.setFieldValue('idSchool', initialValues.idSchool);
    this.props.setFieldValue('groups', initialValues.selectedGroups);
    this.setState({ school: null });
  };

  handleSelectState = event => {
    const { value } = event.target;
    this.props.setFieldValue('state', value);
    this.props.setFieldValue('city', initialValues.city);
    this.props.setFieldValue('idSchool', initialValues.idSchool);
    this.setState({ school: null });
  };

  handleSelectCity = event => {
    const { value } = event.target;
    this.props.setFieldValue('city', value);
    this.props.setFieldValue('idSchool', initialValues.idSchool);
    this.setState({ school: null });
  };

  redirectLogin = () => {
    this.props.history.push('/login');
  };

  renderErrors() {
    const { error } = this.props;
    if (!error || !error.code) return null;
    return (
      <Alert type="danger">
        <FormattedMessage {...(messages[`errorCode${error.code}`] || messages.errorCode500)} />
      </Alert>
    );
  }

  renderAlerts() {
    const { status } = this.props;
    if (status !== EditionStatus.SAVED) return null;
    return (
      <Fragment>
        <Alert type="success" className="text-center">
          <FormattedMessage {...messages.saved}>{txt => <div>{txt}</div>}</FormattedMessage>
        </Alert>
        <FormattedMessage {...messages.login}>
          {txt => (
            <Button color="success" className="btn-block mt-2" onClick={this.redirectLogin}>
              {txt}
            </Button>
          )}
        </FormattedMessage>
      </Fragment>
    );
  }

  handleChangeGroup = selectedGroup => {
    const alreadyAdded = !!find(this.state.selectedGroups, id => id === selectedGroup.id);
    let selectedGroups = [];

    if (alreadyAdded) {
      selectedGroups = this.state.selectedGroups.filter(el => el !== selectedGroup.id);
    } else {
      selectedGroups = [...this.state.selectedGroups, selectedGroup.id];
    }

    this.setState({ selectedGroups });
    this.props.setFieldValue('groups', selectedGroups);
  };

  render() {
    const {
      handleSubmit,
      handleChange,
      handleBlur,
      values,
      isValid,
      schoolsSearch,
      cities,
      states,
      intl,
      selectCountries,
      langCountries,
    } = this.props;
    const { school, groups } = this.state;
    const errors = this.renderErrors();
    const alerts = this.renderAlerts();

    let register = false;

    langCountries.forEach(el => {
      if (el.register === '1' && el.access === '2' && intl.locale === el.language) {
        register = true;
      }
    });

    return (
      <React.Fragment>
        <Loader show={this.state.isLoading} />
        <form className="col ml-auto mr-auto register-form" onSubmit={handleSubmit}>
          <LanguageSelect countries={langCountries} history={this.props.history} />
          <LoginSignTabs register={register} />
          <div className="form-group form-group-mob">
            <Input
              label={intl.formatMessage(messages.nameField)}
              autoComplete="firstname"
              name="firstname"
              valid={this.isValid('firstname')}
              invalidFeedback={this.getMessageError('firstname')}
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
          <div className="form-group form-group-mob">
            <Input
              label={intl.formatMessage(messages.surnameField)}
              autoComplete="lastname"
              name="lastname"
              valid={this.isValid('lastname')}
              invalidFeedback={this.getMessageError('lastname')}
              value={values.surname}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
          <div className="form-group form-group-mob">
            <Input
              label={intl.formatMessage(messages.emailField)}
              autoComplete="email"
              name="email"
              valid={this.isValid('email')}
              invalidFeedback={this.getMessageError('email')}
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
          <div className="form-group form-group-mob">
            <Input
              label={intl.formatMessage(messages.emailConfirmField)}
              autoComplete="email"
              name="emailConfirm"
              valid={this.isValid('emailConfirm')}
              invalidFeedback={this.getMessageError('emailConfirm')}
              value={values.emailConfirm}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
          <div className="form-group form-group-mob">
            <Input
              label={intl.formatMessage(messages.passwordField)}
              type="password"
              autoComplete="password"
              valid={this.isValid('password')}
              invalidFeedback={this.getMessageError('password')}
              name="password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
          <div className="form-group form-group-mob">
            <Input
              label={intl.formatMessage(messages.passwordConfirmField)}
              type="password"
              autoComplete="password"
              valid={this.isValid('passwordConfirm')}
              invalidFeedback={this.getMessageError('passwordConfirm')}
              name="passwordConfirm"
              value={values.passwordConfirm}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>
          <h5 className="label">{intl.formatMessage(messages.profileField)}</h5>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              label={intl.formatMessage(messages.teacher)}
              name="idType"
              value={ID_USER_TYPE_TEACHER}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="professor">
              {intl.formatMessage(messages.teacher)}
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              label={intl.formatMessage(messages.manager)}
              name="idType"
              value={ID_USER_TYPE_COORDINATOR}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="coordenador">
              {intl.formatMessage(messages.manager)}
            </label>
          </div>

          <h5 className="label">{intl.formatMessage(messages.teachingTypes)}</h5>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              label={intl.formatMessage(messages.public)}
              name="teachingType"
              value={ID_USER_TYPE_PUBLIC}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="Público">
              {intl.formatMessage(messages.public)}
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              label={intl.formatMessage(messages.private)}
              name="teachingType"
              value={ID_USER_TYPE_PRIVATE}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="Privado">
              {intl.formatMessage(messages.private)}
            </label>
          </div>
          <Row>
            <Col>
              <FormattedMessage {...messages.country}>
                {txt => (
                  <FormattedMessage {...messages.selectCountryPlaceholder}>
                    {txtPlaceholder => (
                      <Select
                        label={txt}
                        name="idCountry"
                        valid={this.isValid('idCountry')}
                        invalidFeedback={this.getMessageError('idCountry')}
                        selectLabel={txtPlaceholder}
                        value={values.idCountry}
                        options={selectCountries}
                        onChange={this.handleSelectCountry}
                        disabled
                      />
                    )}
                  </FormattedMessage>
                )}
              </FormattedMessage>
            </Col>
            <Col>
              <FormattedMessage {...messages.state}>
                {txt => (
                  <FormattedMessage {...messages.selectStatePlaceholder}>
                    {txtPlaceholder => (
                      <Select
                        label={txt}
                        selectLabel={txtPlaceholder}
                        name="state"
                        disabled={!values.idCountry}
                        value={values.state}
                        valid={this.isValid('state')}
                        invalidFeedback={this.getMessageError('state')}
                        itemValue="state"
                        itemLabel="state"
                        options={states}
                        onChange={this.handleSelectState}
                      />
                    )}
                  </FormattedMessage>
                )}
              </FormattedMessage>
            </Col>
            <Col>
              <FormattedMessage {...messages.city}>
                {txt => (
                  <FormattedMessage {...messages.selectCityPlaceholder}>
                    {txtPlaceholder => (
                      <Select
                        label={txt}
                        selectLabel={txtPlaceholder}
                        itemValue="city"
                        valid={this.isValid('city')}
                        invalidFeedback={this.getMessageError('city')}
                        disabled={!values.state}
                        value={values.city}
                        itemLabel="city"
                        name="city"
                        options={cities}
                        onChange={this.handleSelectCity}
                      />
                    )}
                  </FormattedMessage>
                )}
              </FormattedMessage>
            </Col>
          </Row>
          {/* won't be a necessary
          groups.length > 0 && (
            <Row>
              <Col>
                <h5 className="label">{intl.formatMessage(messages.optionGroupTitle)}</h5>
                {groups.map((el, index) => {
                  const checked = !!find(this.state.selectedGroups, id => id === el.id);
                  return (
                    <div key={el.id} className="form-check form-check-inline">
                      <input
                        id={`group${index}`}
                        className="form-check-input"
                        type="checkbox"
                        label={el.label}
                        name={el.id}
                        value={String(el.id)}
                        checked={checked}
                        onChange={() => this.handleChangeGroup(el)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="professor"
                        onClick={() => {
                          const option = document.getElementById(`group${index}`);
                          option.click();
                        }}
                      >
                        {el.label}
                      </label>
                    </div>
                  );
                })}
              </Col>
            </Row>
          ) */}
          <Row>
            <Col>
              <FormattedMessage {...messages.school}>
                {txt => (
                  <FormattedMessage {...messages.searchSchoolPlaceholder}>
                    {txtPlaceholder => (
                      <FormattedMessage {...messages.noSchoolFounded}>
                        {txtNoSchoolFounded => (
                          <RegisterSchoolSelect
                            label={txt}
                            value={school}
                            valid={this.isValid('school')}
                            invalidFeedback={this.getMessageError('school')}
                            disabled={cities.length === 0}
                            placeholder={txtPlaceholder}
                            noOptionsMessage={() => txtNoSchoolFounded}
                            itemValue="id"
                            itemLabel="name"
                            options={schoolsSearch}
                            onChange={this.handleSelectSchool}
                          />
                        )}
                      </FormattedMessage>
                    )}
                  </FormattedMessage>
                )}
              </FormattedMessage>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="school-add-modal-form-details">
                {school && (
                  <div>
                    <strong>{school.name}</strong>
                    <br />
                    <span>{school.address}</span>
                    <br />
                    <span>{school.zipcode}</span>
                    <br />
                    <span>{school.city}</span> - <span>{school.state}</span>
                  </div>
                )}
                {!school && (
                  <FormattedMessage {...messages.noRegisterSchoolSelected}>
                    {txt => <span>{txt}</span>}
                  </FormattedMessage>
                )}
              </div>
            </Col>
          </Row>
          <Button
            color="primary"
            large
            className="btn-block"
            type="submit"
            value={intl.formatMessage(messages.submit)}
            // disabled={!isValid}
          >
            {intl.formatMessage(messages.submit)}
          </Button>
          {errors}
          {/* {alerts} */}
        </form>
      </React.Fragment>
    );
  }
}

RegisterForm.propTypes = {
  intl: intlShape.isRequired,
};

export default compose(
  enhanceForm,
  injectIntl,
  withRouter,
)(RegisterForm);
